import { FormControl } from '../../../../../widgets/formControl';
import { Preset, SelectablePresets } from '../../../../../widgets/selectablePresets';
import { SwitchGroup } from '../../../../../widgets/switchGroup';
import { showErrorNotification } from '../../../../../widgets/notificationToaster';
import React, { FC, useCallback, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { EditableModelOptions } from 'src/components/widgets/areaDesigner/models';
import ColorSelection from 'src/components/widgets/colorSelection';
import { T, openFileDialog, uploadFile } from 'src/shared/helpers';
import styled from 'styled-components';
import { productBackgroundSectionQuery } from './__generated__/productBackgroundSectionQuery.graphql';
import { Radio } from '../../../../../widgets/radio';
import { getResizableMaterialName } from './resizableMaterialNames';

// Styled components
const BackgroundSectionContainer = styled.div`
    padding-left: 50px;
`;

const SwitchGroupFormControl = styled(FormControl)`
    margin-top: 0;
`;

const RadioButtonsContainer = styled.div`
    margin: 5px 0 10px;
`;

const BackgroundOptionContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px;
    border: 1px solid rgba(219, 226, 230, 1);
    border-radius: 8px;
`;

const FormControlSolidColor = styled(FormControl)`
    > label {
        margin-bottom: 0 !important;
    }
`;

// GraphQL query for background presets
const BACKGROUND_PRESETS_QUERY = graphql`
    query productBackgroundSectionQuery {
        resizableBackgroundPresets {
            presetID
            name
            imageUrl
            thumbnailUrl
        }
        seller {
            sellerID
        }
        generalSettings {
            isSideImageScalingEnabled
        }
    }
`;

interface ProductBackgroundSectionProps {
    editableModelOptions: EditableModelOptions;
    setEditableModelOptions: (options: EditableModelOptions) => void;
}

const ProductBackgroundSection: FC<ProductBackgroundSectionProps> = ({ editableModelOptions, setEditableModelOptions }) => {
    const [isUploading, setIsUploading] = useState(false);
    const [urlCustomBackgroundPresetImg, setUrlCustomBackgroundPresetImg] = useState<string | null | undefined>(
        !editableModelOptions.backgroundPresetID ? editableModelOptions.backgroundImageTempUrl || editableModelOptions.backgroundImageUrl : null
    );
    const query = useLazyLoadQuery<productBackgroundSectionQuery>(BACKGROUND_PRESETS_QUERY, {});

    const backgroundPresets = (query?.resizableBackgroundPresets || []) as Preset[];

    // Map background presets with custom names
    const mappedBackgroundPresets: Preset[] = backgroundPresets.map((preset) => ({
        ...preset,
        name: getResizableMaterialName(preset.presetID, preset.name)
    }));

    // Background type options (excluding "No background" as it's now handled by the switch)
    const backgroundTypeOptions = [
        { value: 1, label: T._('Material', 'Admin') },
        { value: 2, label: T._('Solid color', 'Admin') }
    ];

    // Get current background type
    const currentBackgroundType = editableModelOptions.backgroundType || 0;
    const isBackgroundEnabled = currentBackgroundType > 0;

    // Handle background enable/disable
    const handleBackgroundToggle = useCallback(
        (enabled: boolean) => {
            if (enabled) {
                // Enable background with default solid color type
                setEditableModelOptions({
                    ...editableModelOptions,
                    backgroundType: 1,
                    backgroundColor: '#FFFFFF',
                    backgroundPresetID: null,
                    backgroundImageTempUrl: null,
                    backgroundImageFileObjectID: null,
                    backgroundImageThumbnailFileObjectID: null,
                    backgroundImageThumbnailUrl: null,
                    backgroundImageUrl: null,
                    mirrorType: 'NONE',
                    edgesColor: '#FFFFFF',
                    includeBorderInPrintFiles: false
                });
            } else {
                // Disable background
                setEditableModelOptions({
                    ...editableModelOptions,
                    backgroundType: 0,
                    backgroundColor: null,
                    backgroundPresetID: null,
                    backgroundImageTempUrl: null,
                    backgroundImageFileObjectID: null,
                    backgroundImageThumbnailFileObjectID: null,
                    backgroundImageThumbnailUrl: null,
                    backgroundImageUrl: null,
                    mirrorType: 'COLORED'
                });
            }
        },
        [editableModelOptions, setEditableModelOptions]
    );

    // Handle background type change
    const handleBackgroundTypeChange = useCallback(
        (type: string) => {
            const newType = parseInt(type);
            setEditableModelOptions({
                ...editableModelOptions,
                backgroundType: newType,
                // Reset other background properties when type changes
                backgroundColor: newType === 1 ? '#FFFFFF' : null,
                backgroundPresetID: null,
                backgroundImageTempUrl: null,
                backgroundImageFileObjectID: null,
                backgroundImageThumbnailFileObjectID: null,
                backgroundImageThumbnailUrl: null,
                backgroundImageUrl: null
            });
        },
        [editableModelOptions, setEditableModelOptions]
    );

    // Handle color change
    const handleColorChange = useCallback(
        (index: number, color: string) => {
            setEditableModelOptions({
                ...editableModelOptions,
                backgroundColor: color
            });
        },
        [editableModelOptions, setEditableModelOptions]
    );

    // Handle preset change
    const handlePresetChange = useCallback(
        (presetID: number) => {
            if (presetID == -1 && urlCustomBackgroundPresetImg) {
                setEditableModelOptions({
                    ...editableModelOptions,
                    backgroundImageTempUrl: urlCustomBackgroundPresetImg,
                    backgroundImageFileObjectID: null,
                    backgroundImageThumbnailFileObjectID: null,
                    // Clear preset selection when uploading image
                    backgroundPresetID: null,
                    backgroundImageUrl: null,
                    backgroundImageThumbnailUrl: null
                });

                return;
            }

            const preset = mappedBackgroundPresets.find((p) => p.presetID === presetID);
            setEditableModelOptions({
                ...editableModelOptions,
                backgroundPresetID: presetID,
                backgroundImageUrl: preset?.imageUrl || null,
                backgroundImageThumbnailUrl: preset?.thumbnailUrl || null,
                // Clear uploaded image when selecting preset
                backgroundImageTempUrl: null,
                backgroundImageFileObjectID: null,
                backgroundImageThumbnailFileObjectID: null
            });
        },
        [editableModelOptions, setEditableModelOptions, mappedBackgroundPresets]
    );

    // Handle image upload
    const handleImageUpload = useCallback(() => {
        if (urlCustomBackgroundPresetImg) {
            setEditableModelOptions({
                ...editableModelOptions,
                backgroundImageTempUrl: urlCustomBackgroundPresetImg,
                backgroundImageFileObjectID: null,
                backgroundImageThumbnailFileObjectID: null,
                // Clear preset selection when uploading image
                backgroundPresetID: null,
                backgroundImageUrl: null,
                backgroundImageThumbnailUrl: null
            });
        }

        openFileDialog(async (files) => {
            if (files.length === 0) {
                if (urlCustomBackgroundPresetImg) {
                    setEditableModelOptions({
                        ...editableModelOptions,
                        backgroundImageTempUrl: urlCustomBackgroundPresetImg,
                        backgroundImageFileObjectID: null,
                        backgroundImageThumbnailFileObjectID: null,
                        // Clear preset selection when uploading image
                        backgroundPresetID: null,
                        backgroundImageUrl: null,
                        backgroundImageThumbnailUrl: null
                    });
                }
                return;
            }

            const file = files[0];
            try {
                setIsUploading(true);
                const uploadResult = await uploadFile(file, query.generalSettings.isSideImageScalingEnabled);
                setUrlCustomBackgroundPresetImg(uploadResult);
                setEditableModelOptions({
                    ...editableModelOptions,
                    backgroundImageTempUrl: uploadResult,
                    backgroundImageFileObjectID: null,
                    backgroundImageThumbnailFileObjectID: null,
                    // Clear preset selection when uploading image
                    backgroundPresetID: null,
                    backgroundImageUrl: null,
                    backgroundImageThumbnailUrl: null
                });
            } catch (error) {
                console.error('Failed to upload background image:', error);
                showErrorNotification({
                    message: T._('Failed to upload background image', 'Admin')
                });
            } finally {
                setIsUploading(false);
            }
        }, 'image/*');
    }, [editableModelOptions, setEditableModelOptions, query.generalSettings.isSideImageScalingEnabled]);

    return (
        <>
            <SwitchGroupFormControl>
                <SwitchGroup checked={isBackgroundEnabled} onChange={handleBackgroundToggle} label={T._('Enable background', 'Admin')} />
            </SwitchGroupFormControl>

            {isBackgroundEnabled && (
                <BackgroundSectionContainer>
                    <RadioButtonsContainer>
                        {backgroundTypeOptions.map((option) => (
                            <Radio
                                key={option.value}
                                name='backgroundType'
                                value={option.value.toString()}
                                checked={currentBackgroundType === option.value}
                                onSelect={() => handleBackgroundTypeChange(option.value.toString())}
                                label={option.label}
                            />
                        ))}
                    </RadioButtonsContainer>

                    {isBackgroundEnabled && currentBackgroundType === 1 && (
                        <BackgroundOptionContainer>
                            <SelectablePresets
                                presets={mappedBackgroundPresets}
                                selectedPresetID={editableModelOptions.backgroundPresetID}
                                uploadedImageUrl={urlCustomBackgroundPresetImg}
                                onPresetSelect={handlePresetChange}
                                onImageUpload={handleImageUpload}
                                isUploading={isUploading}
                                title={T._('Choose a material texture from our provided presets or upload your own', 'Admin')}
                                uploadLabel={T._('Upload', 'Admin')}
                                uploadingLabel={T._('Uploading...', 'Admin')}
                                refreshUploadTooltip={T._('Click to update custom background image', 'Admin')}
                            />
                        </BackgroundOptionContainer>
                    )}

                    {isBackgroundEnabled && currentBackgroundType === 2 && (
                        <FormControlSolidColor label={T._('Background color', 'Admin')}>
                            <ColorSelection colors={[editableModelOptions.backgroundColor || '#FFFFFF']} onChange={handleColorChange} menuPosition='fixed' />
                        </FormControlSolidColor>
                    )}
                </BackgroundSectionContainer>
            )}
        </>
    );
};

export default ProductBackgroundSection;
